import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Building2, Save, X, UserPlus, UserMinus, UserCheck, Shield, MapPin, Globe, Phone, Mail, Activity, Users, Zap } from 'lucide-react';
import { Button, FormInput, FormSelect, FormTextArea, AdminPageLayout, Section, Card, Grid, Col, Badge, PhoneNumberInput } from '../../components';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';

const LOCATION_TYPE_OPTIONS = [
  { value: 'main_facility', label: 'Main Facility' },
  { value: 'branch_facility', label: 'Branch Facility' },
  { value: 'training_ground', label: 'Training Ground' },
  { value: 'academy', label: 'Academy' },
  { value: 'community_center', label: 'Community Center' }
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' }
];

const AGE_CALCULATION_OPTIONS = [
  { value: 'calendar_year', label: 'Calendar Year (Age as of Dec 31)' },
  { value: 'birth_year', label: 'Birth Year' },
  { value: 'program_start', label: 'Program Start Date' },
  { value: 'registration_date', label: 'Registration Date' },
  { value: 'custom_date', label: 'Custom Cutoff Date' }
];

const US_STATE_OPTIONS = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

const EditBranch = () => {
  const navigate = useNavigate();
  const { branch_id } = useParams();
  const { showSuccess, showError } = useToast();

  const [originalBranch, setOriginalBranch] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [adminMode, setAdminMode] = useState('keep'); // 'keep', 'remove', 'change', 'create', 'assign'
  const [potentialAdmins, setPotentialAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    location_type: 'training_ground',
    status: 'active',
    description: '',
    website: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'US',
    timezone: 'America/New_York',
    currency: 'USD',
    age_calculation_method: 'calendar_year',
    age_cutoff_date: null,
    admin_first_name: '',
    admin_last_name: '',
    admin_email: '',
    admin_phone: '',
    admin_password: '',
    assign_existing_user_id: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadBranch = useCallback(async () => {
    try {
      setLoading(true);
      const branch = await apiService.getBranch(branch_id);
      setOriginalBranch(branch);

      setFormData(prev => ({
        ...prev,
        ...branch,
        // Ensure defaults if null
        display_name: branch.display_name || '',
        description: branch.description || '',
        website: branch.website || '',
        address_line2: branch.address_line2 || '',
        age_cutoff_date: branch.age_cutoff_date || null
      }));

      if (branch.admin_user_id) {
        try {
          const adminData = await apiService.getUser(branch.admin_user_id);
          setAdminUser(adminData);
        } catch (err) {
          console.error('Error loading admin user');
        }
      }
    } catch (err) {
      showError(`Failed to load branch: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [branch_id, showError]);

  useEffect(() => {
    loadBranch();
  }, [loadBranch]);

  const loadPotentialAdmins = useCallback(async () => {
    setLoadingAdmins(true);
    try {
      const response = await apiService.getPotentialBranchAdmins();
      setPotentialAdmins(response.users || []);
    } catch (error) {
      showError('Failed to load potential branch administrators');
      setPotentialAdmins([]);
    } finally {
      setLoadingAdmins(false);
    }
  }, [showError]);

  useEffect(() => {
    if (adminMode === 'assign' || adminMode === 'change') {
      loadPotentialAdmins();
    }
  }, [adminMode, loadPotentialAdmins]);

  const handleAdminModeChange = (e) => {
    const newMode = e.target.value;
    setAdminMode(newMode);
    setFormData(prev => ({
      ...prev,
      admin_first_name: '',
      admin_last_name: '',
      admin_email: '',
      admin_phone: '',
      admin_password: '',
      assign_existing_user_id: ''
    }));
    setFieldErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Branch designation is required';
    if (!formData.email.trim()) errors.email = 'Operational email is required';
    if (!formData.phone.trim()) errors.phone = 'Contact line is required';
    if (!formData.address_line1.trim()) errors.address_line1 = 'Site address is required';
    if (!formData.city.trim()) errors.city = 'City is required';
    if (!formData.state.trim()) errors.state = 'State is required';
    if (!formData.zip_code.trim()) errors.zip_code = 'ZIP code is required';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (adminMode === 'create') {
      if (!formData.admin_first_name?.trim()) errors.admin_first_name = 'First name required';
      if (!formData.admin_last_name?.trim()) errors.admin_last_name = 'Last name required';
      if (!formData.admin_email?.trim()) errors.admin_email = 'Admin email required';
      else if (!emailRegex.test(formData.admin_email)) errors.admin_email = 'Invalid admin email';
      if (formData.admin_phone && formData.admin_phone.trim() && formData.admin_phone.replace(/\D/g, '').length < 10) {
        errors.admin_phone = 'Phone must be at least 10 digits';
      }
      if (!formData.admin_password) errors.admin_password = 'Password required';
      else if (formData.admin_password.length < 8) errors.admin_password = 'Min 8 characters';
    } else if (adminMode === 'assign' || adminMode === 'change') {
      if (!formData.assign_existing_user_id) errors.assign_existing_user_id = 'Authority assignment required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      const updates = {};
      Object.keys(formData).forEach(key => {
        if (key.startsWith('admin_') || key === 'assign_existing_user_id') return;
        if (formData[key] !== originalBranch[key]) updates[key] = formData[key];
      });

      if (adminMode === 'create') {
        updates.admin_first_name = formData.admin_first_name;
        updates.admin_last_name = formData.admin_last_name;
        updates.admin_email = formData.admin_email;
        // Only send admin_phone if it has a value (backend requires min 10 chars if provided)
        if (formData.admin_phone && formData.admin_phone.trim()) {
          updates.admin_phone = formData.admin_phone;
        }
        updates.admin_password = formData.admin_password;
      } else if (adminMode === 'assign' || adminMode === 'change') {
        updates.assign_existing_user_id = formData.assign_existing_user_id;
      } else if (adminMode === 'remove') {
        updates.remove_admin = true;
      }

      await apiService.updateBranch(branch_id, updates);
      showSuccess('Territory configuration synchronized successfully.');
      navigate(`/admin/branches/${branch_id}`);
    } catch (error) {
      showError(`Synchronisation failed: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout title="REFINE TERRITORY" maxWidth="1400px">
        <div style={{ padding: '4rem', textAlign: 'center', opacity: 0.6 }}>Synchronizing regional nodes...</div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="REFINE TERRITORY"
      subtitle={`Modifying ${originalBranch?.name}`}
      backPath={`/admin/branches/${branch_id}`}
      maxWidth="1400px"
    >
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>

          <Section title="Territory Identity">
            <Card level={1}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <Grid columns={2} gap="1.5rem">
                  <Col span={1}>
                    <FormInput
                      label="Branch Designation *"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      error={fieldErrors.name}
                      required
                      placeholder="e.g., Richmond Elite Academy"
                    />
                  </Col>
                  <Col span={1}>
                    <FormInput
                      label="Custom Display Title"
                      name="display_name"
                      value={formData.display_name}
                      onChange={handleInputChange}
                      error={fieldErrors.display_name}
                      placeholder="e.g., Richmond East"
                    />
                  </Col>
                </Grid>

                <FormTextArea
                  label="Mission Narrative"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  error={fieldErrors.description}
                  rows={3}
                  placeholder="Describe the operational focus and values of this location..."
                />

                <Grid columns={2} gap="1.5rem">
                  <Col span={1}>
                    <FormSelect
                      label="Infrastructure Type *"
                      name="location_type"
                      value={formData.location_type}
                      onChange={handleInputChange}
                      options={LOCATION_TYPE_OPTIONS}
                      error={fieldErrors.location_type}
                      required
                    />
                  </Col>
                  <Col span={1}>
                    <FormSelect
                      label="Deployment Status *"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      options={STATUS_OPTIONS}
                      error={fieldErrors.status}
                      required
                    />
                  </Col>
                </Grid>
              </div>
            </Card>
          </Section>

          <Section title="Communication Layer">
            <Card level={1}>
              <Grid columns={3} gap="1.5rem">
                <Col span={1}>
                  <FormInput
                    label="Official Liaison Email *"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={fieldErrors.email}
                    required
                    placeholder="hq@location.com"
                  />
                </Col>
                <Col span={1}>
                  <FormInput
                    label="Direct Contact Line *"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    error={fieldErrors.phone}
                    required
                    placeholder="(555) 123-4567"
                  />
                </Col>
                <Col span={1}>
                  <FormInput
                    label="Digital Presence (URL)"
                    name="website"
                    type="url"
                    value={formData.website}
                    onChange={handleInputChange}
                    error={fieldErrors.website}
                    placeholder="https://www.location.com"
                  />
                </Col>
              </Grid>
            </Card>
          </Section>

          <Section title="Spatial Coordinates">
            <Card level={1}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <FormInput
                  label="Primary Site Address *"
                  name="address_line1"
                  value={formData.address_line1}
                  onChange={handleInputChange}
                  error={fieldErrors.address_line1}
                  required
                  placeholder="123 Elite Way"
                />
                <FormInput
                  label="Building / Suite Identifier"
                  name="address_line2"
                  value={formData.address_line2}
                  onChange={handleInputChange}
                  error={fieldErrors.address_line2}
                  placeholder="e.g., Suite 500"
                />
                <Grid columns={3} gap="1.5rem">
                  <Col span={1}>
                    <FormInput
                      label="Metropolitan Area *"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      error={fieldErrors.city}
                      required
                      placeholder="Richmond"
                    />
                  </Col>
                  <Col span={1}>
                    <FormSelect
                      label="State Authority *"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      options={US_STATE_OPTIONS}
                      error={fieldErrors.state}
                      required
                    />
                  </Col>
                  <Col span={1}>
                    <FormInput
                      label="ZIP Code *"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleInputChange}
                      error={fieldErrors.zip_code}
                      required
                      placeholder="23220"
                    />
                  </Col>
                </Grid>
              </div>
            </Card>
          </Section>

          <Section title="Administrative Authority">
            <Card level={1}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {adminUser && (
                  <div style={{
                    padding: '1.25rem',
                    background: 'var(--surface-lvl2)',
                    borderRadius: '12px',
                    border: '1px solid var(--color-border-light)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}>
                    <div style={{ width: '40px', height: '40px', background: 'var(--surface-lvl3)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Shield size={20} style={{ color: 'var(--color-primary)' }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{adminUser.first_name} {adminUser.last_name}</div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.6 }}>Current Territory Administrator ({adminUser.email})</div>
                    </div>
                  </div>
                )}

                <FormSelect
                  label="Authority Lifecycle Management"
                  name="adminMode"
                  value={adminMode}
                  onChange={handleAdminModeChange}
                  options={
                    adminUser ? [
                      { value: 'keep', label: 'Retain Current Authority' },
                      { value: 'change', label: 'Redirect Authority to Existing User' },
                      { value: 'remove', label: 'Suspend Local Authority' }
                    ] : [
                      { value: 'keep', label: 'Under Club Governance' },
                      { value: 'create', label: 'Provision New Administrator' },
                      { value: 'assign', label: 'Assign Authority to Existing User' }
                    ]
                  }
                />

                {adminMode === 'create' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', padding: '1.5rem', background: 'var(--surface-lvl2)', borderRadius: '12px', border: '1px solid var(--color-border-light)' }}>
                    <Grid columns={2} gap="1.5rem">
                      <Col span={1}><FormInput label="First Name" name="admin_first_name" value={formData.admin_first_name} onChange={handleInputChange} error={fieldErrors.admin_first_name} required /></Col>
                      <Col span={1}><FormInput label="Last Name" name="admin_last_name" value={formData.admin_last_name} onChange={handleInputChange} error={fieldErrors.admin_last_name} required /></Col>
                    </Grid>
                    <Grid columns={2} gap="1.5rem">
                      <Col span={1}><FormInput label="Authority Email" name="admin_email" type="email" value={formData.admin_email} onChange={handleInputChange} error={fieldErrors.admin_email} required /></Col>
                      <Col span={1}><PhoneNumberInput label="Phone Number (Optional)" name="admin_phone" value={formData.admin_phone} onChange={handleInputChange} error={fieldErrors.admin_phone} placeholder="(555) 123-4567" /></Col>
                    </Grid>
                    <Grid columns={2} gap="1.5rem">
                      <Col span={1}><FormInput label="Secure Password" name="admin_password" type="password" value={formData.admin_password} onChange={handleInputChange} error={fieldErrors.admin_password} required /></Col>
                      <Col span={1}></Col>
                    </Grid>
                  </div>
                )}

                {(adminMode === 'assign' || adminMode === 'change') && (
                  <div style={{ padding: '1.5rem', background: 'var(--surface-lvl2)', borderRadius: '12px', border: '1px solid var(--color-border-light)' }}>
                    <FormSelect
                      label="Select Authorized User"
                      name="assign_existing_user_id"
                      value={formData.assign_existing_user_id}
                      onChange={handleInputChange}
                      options={[
                        { value: '', label: 'Select registry member...' },
                        ...potentialAdmins.filter(u => u.id !== adminUser?.id).map(user => ({
                          value: user.id,
                          label: `${user.full_name} (${user.email})`
                        }))
                      ]}
                      error={fieldErrors.assign_existing_user_id}
                      required
                    />
                  </div>
                )}
              </div>
            </Card>
          </Section>

          <Section title="Governance Protocols">
            <Card level={1}>
              <Grid columns={2} gap="1.5rem">
                <Col span={1}>
                  <FormSelect
                    label="Age Verification Protocol"
                    name="age_calculation_method"
                    value={formData.age_calculation_method}
                    onChange={handleInputChange}
                    options={AGE_CALCULATION_OPTIONS}
                    error={fieldErrors.age_calculation_method}
                  />
                </Col>
                {formData.age_calculation_method === 'custom_date' && (
                  <Col span={1}>
                    <FormInput
                      label="Protocol Threshold Date"
                      name="age_cutoff_date"
                      type="date"
                      value={formData.age_cutoff_date || ''}
                      onChange={handleInputChange}
                      error={fieldErrors.age_cutoff_date}
                    />
                  </Col>
                )}
              </Grid>
            </Card>
          </Section>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            padding: '2rem',
            background: 'var(--surface-lvl1)',
            borderRadius: '16px',
            border: '1px solid var(--color-border-light)'
          }}>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/admin/branches/${branch_id}`)}
              disabled={saving}
            >
              Cancel Refinement
            </Button>
            <Button
              type="submit"
              variant="save"
              loading={saving}
            >
              Synchronize Configuration
            </Button>
          </div>
        </div>
      </form>
    </AdminPageLayout>
  );
};

export default EditBranch;
